import { BottomNavigation, BottomNavigationAction, Box, TextField, Tooltip } from '@mui/material';
import List from 'widgets/list/list';
import styles from 'pages/tasks/Tasks.module.css';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { useNavigate } from 'react-router-dom';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContractsIDTasks, CurrentPageTasksInCase, LastFetchTasks, LoadingTasks, TasksCount, TasksList } from 'entities/tasks/selector';
import { fetchTasksList } from 'entities/tasks/actions/fetch-tasks-list';
import { setCurrentPageInCaseTasks } from 'entities/tasks';
import CustomButton from 'shared/custom-button/CustomButton';
import { isEqual } from 'lodash-es';
import { allFields, defaultFields, fieldMappings } from 'features/tasks/utils/constant';
import SettingsIcon from '@mui/icons-material/Settings';
import ModalWidget from 'widgets/modal/Modal';
import TransferList from 'widgets/field-settings/FieldSettings';
import SearchIcon from '@mui/icons-material/Search';
import { ITasks } from 'entities/tasks/types/Tasks-interface';

import appStyles from 'app/providers/theme/App.module.css';
import { isValidIdCase } from 'shared/utils/hooks/useValidationIdCase';
import Loader from 'shared/loader/Loader';

interface ITasksProps {
    idContract: string[];
}

export const TasksCase: FC<ITasksProps> = ({ idContract }) => {
    const limit = 10;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const allTasks = useAppSelector(TasksList);
    const isLoading = useAppSelector(LoadingTasks);
    const currentPageInCase = useAppSelector(CurrentPageTasksInCase);

    const fetchContractsID = useAppSelector(ContractsIDTasks, isEqual);

    const isFetching = useRef(false);
    const stableIdContract = useMemo(() => idContract || [], [idContract]);
    const oneDay = 24 * 60 * 60 * 1000;
    const currentContracts = stableIdContract;

    const lastFetch = useAppSelector(LastFetchTasks);
    const shouldFetch = () => {
        const isSameParams = isEqual(currentContracts, fetchContractsID);
        const isDataStale = !lastFetch || (Date.now() - lastFetch > oneDay);

        return !isSameParams || isDataStale;
    };

    const [selectedOptions, setSelectedOptions] = useState('Весь список');
    const [isUserAction, setIsUserAction] = useState(false);

    const sortArray: string[] | [] = [];

    const [fields, setFields] = useState<string[]>(defaultFields);
    const [tempFields, setTempFields] = useState<string[]>(fields);
    const [open, setOpen] = useState(false);
    const [openSearch, setOpenSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const handleOpenSearch = () => setOpenSearch((prevState) => !prevState);

    const [isValid, setIsValid] = useState(true);

    const fetchTasksWithValidation = (contractID: string[]) => {
        if (contractID.every(isValidIdCase)) {
            const filters = selectedOptions === 'Весь список' ? [] : [];
            dispatch(fetchTasksList({ contractID, sortArray, filters }));
        } else {
            setIsValid(false);
            console.error('Invalid idCase');
        }
    };

    useEffect(() => {
        if (isFetching.current) return;
        if (shouldFetch()) {
            isFetching.current = true;
            new Promise<void>((resolve) => {
                fetchTasksWithValidation(stableIdContract);
                resolve();
            }).finally(() => {
                isFetching.current = false;
            });
        }
    }, [dispatch, stableIdContract, lastFetch, fetchContractsID]);

    useEffect(() => {
        if (isUserAction) {
            dispatch(setCurrentPageInCaseTasks({ page: 1 }));
            setIsUserAction(false);
        }
    }, [selectedOptions, isUserAction]);

    const handleChildAction = useCallback((page: number) => {
        dispatch(setCurrentPageInCaseTasks({ page }));
    }, [dispatch]);

    const handleClickNewTask = () => {
        if (idContract) {
            navigate(`/createTask/?id=${idContract}`, {
                state: {
                    fromInsuranceContractPage: true,
                    id: idContract ? idContract[0] : undefined,
                    activeTab: 2
                }
            });
        } else {
            navigate(`/createTask`);
        }
    }

    const renderItem = (item: any) => (
        <Box sx={{
            display: 'flex',
            width: '100%',
            position: 'relative',
        }}>
            {item.UnreadMessagesCounter !== 0 && (
                <Tooltip title="Непрочитанные сообщения" placement="top">
                    <Box sx={{
                        position: 'absolute',
                        left: '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'red',
                        color: 'white',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        zIndex: 1, 
                    }}>
                        {item.UnreadMessagesCounter}
                    </Box>
                </Tooltip>

            )}
            {fields.map((field) => {
                const key = fieldMappings[field];
                return (
                    <Box key={field} sx={{ flex: 1 }}>
                        <p>{item[key]}</p>
                    </Box>
                );
            })}
        </Box>
    );

    const handleOpenModal = () => {
        setTempFields(fields);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleReset = () => {
        setTempFields(defaultFields);
    };

    const handleAccept = () => {
        setFields(tempFields);
        setOpen(false);
    };

    const handleTempFieldsChange = (newFields: string[]) => {
        setTempFields(newFields);
    };

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        dispatch(setCurrentPageInCaseTasks({ page: 1 }));
    }, [dispatch]);

    const filteredTasks = (() => {
        let filteredData = [...allTasks];
        if (selectedOptions !== 'Весь список') {
            filteredData = filteredData.filter(task => task.StatusName === selectedOptions);
        }
        if (searchQuery) {
            filteredData = filteredData.filter((item: ITasks) =>
                item.Number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.InsurerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.ThemeName.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }
        return filteredData;
    })();

    const paginatedTasks = () => {
        const startIndex = (currentPageInCase - 1) * limit;
        return filteredTasks.slice(startIndex, startIndex + limit);
    };

    const handleUpdate = () => {
        isFetching.current = true;
        fetchTasksWithValidation(stableIdContract);
        isFetching.current = false;

    }

    if (!isValid) {
        return (
            <p className={styles.errorText}>Некорректный ID договора</p>
        )
    }
    return (
        <Box>
            {isLoading && (
                <Loader
                    title='Загрузка...'
                />
            )}
            {!isLoading && (
                <>
                    <Box className={styles.btnNavigation}>
                        <CustomButton
                            title="Новая заявка"
                            handleClick={handleClickNewTask}
                            textColor="white"
                            backgroundColor="#0054E6"
                            padding="5px 30px"
                            gap="10px"
                            border='none'
                            borderRadius='4px'
                        />
                        <Box className={styles.rightBtnNavigation}>
                            <BottomNavigation
                                showLabels
                                value={selectedOptions}
                                onChange={(event, newValue) => {
                                    setSelectedOptions(newValue);
                                    setIsUserAction(true);
                                }}
                                sx={{
                                    backgroundColor: 'transparent',
                                }}
                            >
                                <BottomNavigationAction label="Весь список" value="Весь список" sx={{
                                    '&.Mui-selected': {
                                        color: '#0054E6 !important',
                                    },
                                }} />
                                <BottomNavigationAction label="В работе" value="В работе" sx={{
                                    '&.Mui-selected': {
                                        color: '#0054E6 !important',
                                    },
                                }} />
                                <BottomNavigationAction label="Черновики" value="Проект" sx={{
                                    '&.Mui-selected': {
                                        color: '#0054E6 !important',
                                    },
                                }} />
                                <BottomNavigationAction label="Выполнено" value="Выполнено" sx={{
                                    '&.Mui-selected': {
                                        color: '#0054E6 !important',
                                    },
                                }} />
                                <BottomNavigationAction label="Отмененные" value="Отмененные" sx={{
                                    '&.Mui-selected': {
                                        color: '#0054E6 !important',
                                    },
                                }} />
                            </BottomNavigation>
                            <Tooltip title="Настройка таблицы" placement="top">
                                <span
                                    className={styles.iconContract}
                                    onClick={() => handleOpenModal()}
                                >
                                    <SettingsIcon />
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                    <ModalWidget
                        open={open}
                        handleClose={handleClose}
                        title="Настройка полей таблицы"
                        closeIconPosition="right"
                        showAcceptButton
                        showCancelButton
                        onAccept={handleAccept}
                        onCancel={handleReset}
                        widthProps={600}
                    >
                        <TransferList
                            key={JSON.stringify(tempFields)}
                            defaultFields={tempFields}
                            allFields={allFields}
                            onFieldsChange={handleTempFieldsChange}
                        />
                    </ModalWidget>
                    <Box className={appStyles.iconSort}>
                        <span onClick={handleOpenSearch} className={`${openSearch ? appStyles.iconSelected : ''}`}><SearchIcon /></span>
                    </Box>
                    {openSearch && (
                        <TextField
                            label="Поиск"
                            variant="outlined"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            fullWidth
                            margin="normal"
                        />
                    )}
                    <Box sx={{
                        width: '100%',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        {allTasks.length > 0 && filteredTasks.length > 0 ? (
                            <List
                                title={fields}
                                data={paginatedTasks()}
                                renderItem={renderItem}
                                renderPagination={filteredTasks.length > limit}
                                pageCount={Math.ceil(filteredTasks.length / limit)}
                                onAction={handleChildAction}
                                currentPage={currentPageInCase}
                                pathnameProps="/tasks/case/"
                                link={true}
                            />
                        ) : (
                            <Box className={appStyles.infoBox}>
                                <p>Заявки не найдены</p>
                            </Box>
                        )}
                        <Box className={styles.updateBtnBox}>
                            <CustomButton
                                handleClick={handleUpdate}
                                title='Обновить данные'
                                textColor="#0054E6"
                                backgroundColor="transparent"
                                padding="5px 30px"
                                gap="10px"
                                border='none'
                            />
                        </Box>

                    </Box>
                </>
            )}

        </Box>
    );
}

export default TasksCase;
